import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-hoodies';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Заказ худи и свитеров через YOYbuy Spreadsheet: гид по тканям, размерам и качеству',
  description: 'Всё о заказе худи, свитшотов, толстовок и свитеров из Китая с помощью yoybuy spreadsheet. Как выбрать ткань, не ошибиться с размером и получить качественную вещь.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20photography%20of%20a%20beige%20oversized%20hoodie%20draped%20over%20a%20wooden%20chair%20in%20a%20bright%20sunlit%20room%20with%20white%20walls%2C%20natural%20texture%20of%20cotton%20fleece%20fabric%20visible%2C%20warm%20neutral%20tones%2C%20soft%20shadows%20from%20window%20light%2C%20editorial%20clothing%20photography%20style%2C%20clean%20aesthetic%2C%20high%20detail%20on%20stitching%20and%20fabric%20weave&width=1400&height=700&seq=article-hoodies-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function HoodiesArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Худи и свитеры</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Заказ худи и свитеров через YOYbuy Spreadsheet: гид по тканям, размерам и качеству
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Всё о заказе худи, свитшотов, толстовок и свитеров из Китая с помощью <strong>yoybuy spreadsheet</strong>. Как выбрать ткань, не ошибиться с размером и получить качественную вещь.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 7 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20photography%20of%20a%20beige%20oversized%20hoodie%20draped%20over%20a%20wooden%20chair%20in%20a%20bright%20sunlit%20room%20with%20white%20walls%2C%20natural%20texture%20of%20cotton%20fleece%20fabric%20visible%2C%20warm%20neutral%20tones%2C%20soft%20shadows%20from%20window%20light%2C%20editorial%20clothing%20photography%20style%2C%20clean%20aesthetic%2C%20high%20detail%20on%20stitching%20and%20fabric%20weave&width=1400&height=700&seq=article-hoodies-hero-2026&orientation=landscape"
                  alt="Заказ худи и свитеров из Китая через YOYbuy Sheet"
                  title="YOYbuy Sheet худи — полный гид по заказу свитеров из Китая"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Почему худи и свитеры из Китая — это выгодно
                  </h2>
                  <p>
                    Худи, свитшоты и свитеры — одна из самых востребованных категорий при заказе из Китая. Местные фабрики производят одежду для десятков мировых брендов, и качество тканей зачастую не уступает европейским аналогам. При этом цена — в 2–4 раза ниже розничной. А с <strong>yoybuy spreadsheet</strong> процесс заказа становится максимально простым и прозрачным.
                  </p>
                  <p>
                    В <strong>yoybuy sheet</strong> для категории «Худи / Свитеры» предусмотрены специальные колонки: тип ткани (флис, футер, хлопок), плотность материала (граммовка), наличие капюшона, тип кармана и длина рукава. Это позволяет агенту точно понять, что именно вы хотите заказать, и исключает недопонимание. Вы просто заполняете таблицу — и получаете именно то, что ожидали.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как выбрать ткань для худи: полный разбор в YOYbuy Sheet
                  </h2>
                  <p>
                    Качество худи на 90% определяется тканью. В <strong>yoybuy spreadsheet</strong> вы можете детально описать требования к материалу, чтобы агент выбрал лучший вариант. Разберём основные типы тканей:
                  </p>
                  <div className="space-y-4">
                    {[
                      { name: 'Флис (Fleece)', desc: 'Самый популярный материал для худи. Мягкий, тёплый, приятный на ощупь. В yoybuy sheet укажите плотность флиса: 280–330 г/м² для тёплых худи, 200–260 г/м² для облегчённых моделей.' },
                      { name: 'Футер (French Terry)', desc: 'Натуральная хлопковая ткань с начёсом. Идеальна для повседневной носки. В таблице YOYbuy отметьте, нужен ли вам двухниточный или трёхниточный футер — трёхниточный плотнее и теплее.' },
                      { name: 'Хлопок 100%', desc: 'Лёгкий, дышащий материал для летних свитшотов. Укажите в yoybuy spreadsheet желаемую плотность: от 180 г/м² для тонких моделей до 260 г/м² для более плотных.' },
                      { name: 'Смесовые ткани', desc: 'Хлопок + полиэстер. Меньше мнутся, быстрее сохнут. В таблице YOYbuy укажите желаемое соотношение, например 80/20 или 60/40.' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-accent-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20photography%20of%20folded%20cream%20and%20beige%20hoodies%20on%20a%20light%20textured%20linen%20surface%2C%20fabric%20swatches%20and%20color%20palette%20cards%20arranged%20aesthetically%2C%20soft%20diffused%20natural%20lighting%2C%20neutral%20warm%20color%20scheme%2C%20professional%20textile%20photography%2C%20clean%20minimal%20composition%2C%20high%20detail%20on%20texture%20and%20weave&width=1200&height=600&seq=article-hoodies-fabric&orientation=landscape"
                    alt="YOYbuy Sheet — выбор ткани для худи, образцы материалов"
                    title="Выбор ткани в YOYbuy Spreadsheet для заказа худи из Китая"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Размеры худи: как не ошибиться при заказе через YOYbuy Sheet
                  </h2>
                  <p>
                    Китайские размеры худи и свитеров традиционно меньше европейских на 1–2 размера. Но с <strong>yoybuy spreadsheet</strong> это не проблема. В таблице есть специальные поля для указания ваших реальных параметров: обхват груди, длина рукава, желаемая длина изделия. Агент, получив такую таблицу, точно подберёт размер по сетке конкретного продавца.
                  </p>
                  <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-200 my-6">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">Лайфхак для точного размера:</h3>
                    <p className="text-sm text-foreground-600">
                      Измерьте своё любимое худи, которое сидит идеально, и укажите его параметры в <strong>yoybuy sheet</strong> в поле «Желаемые размеры изделия». Агент подберёт китайский размер, максимально близкий к вашим замерам — это работает точнее, чем конвертация размерных сеток.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Тренды: какие худи сейчас популярны
                  </h2>
                  <p>
                    Через <strong>yoybuy spreadsheet</strong> можно заказать любые модели: от базовых однотонных худи до лимитированных дизайнерских коллабораций. Сейчас в тренде оверсайз-силуэты, худи с крупными принтами на спине, модели с эффектом стирки и винтажные фасоны. Китайские производители быстро реагируют на тренды — новинки появляются на складах раньше, чем доходят до европейских магазинов. А с <strong>yoybuy sheet</strong> вы получаете их по ценам производителя, без наценок ритейла.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Сколько худи можно заказать в одной посылке
                  </h2>
                  <p>
                    Вес одного худи — примерно 400–700 граммов (в зависимости от плотности ткани). Через <strong>yoybuy spreadsheet</strong> вы можете рассчитать оптимальное количество: таблица автоматически суммирует вес всех позиций и показывает примерную стоимость доставки. Обычно в одну посылку помещается 3–7 худи без существенного увеличения стоимости доставки за единицу. Это делает оптовый заказ особенно выгодным.
                  </p>
                  <p>
                    Хотите узнать больше о других категориях? Читайте наше <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">полное руководство по YOYbuy Sheet</Link> или вернитесь на <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">главную страницу</Link> для выбора категории.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shoes" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ обуви</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-jackets" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ курток</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-xs text-primary-600 hover:text-primary-700 underline">Худи на kaspreadsheet</a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}